import i18n from '@/utils/i18n';
import { BookmarkAction } from '@sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
  PricingPageSource,
} from '@sales-portal/domain/pricing/analytics/pricing-management-analytics';
import {
  PricingTableQuickAction,
  QuickActionShortcutKey,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-table-quick-actions-popover';
import {
  selectBookmarkNote,
  selectIsBookmarked,
  usePricingBookmarksStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import { useBookmarkMutator } from '@sales-portal/domain/pricing/blocs/mutators/use-bookmark-mutator';
import { useCallback, useMemo, useState } from 'react';

export interface BookmarkPair {
  restaurant_id: string;
  variant_pack_id: string;
}

// Mirrors the inline/bulk split the edit events use: a row's own pricing cell
// reports singular UUIDs, the multi-selection bar reports deduped arrays.
export enum BookmarkSurface {
  Bulk = 'bulk',
  Inline = 'inline',
}

// Core bookmark wiring for one or more (variant pack, restaurant) pairs — shared
// by the single-row cells (item + agent) and the bulk multi-selection bar. If
// any pair is already bookmarked, only Unbookmark is offered; otherwise Bookmark
// (gated on access). Bookmark optimistically flips the store (instant icon) and
// opens a single shared note popover — the upsert fires once, on note-close, so
// no note means no wasted second write.
export const useBookmarkActionForPairs = ({
  canBookmarkItem,
  pageSource,
  pairs,
  supplierUUIDs,
  surface = BookmarkSurface.Bulk,
}: {
  canBookmarkItem: boolean;
  pageSource: PricingPageSource;
  pairs: BookmarkPair[];
  supplierUUIDs: string[];
  surface?: BookmarkSurface;
}) => {
  const { bookmark, unbookmark } = useBookmarkMutator();
  const setBookmark = usePricingBookmarksStore((store) => store.setBookmark);
  const [isNoteOpen, setIsNoteOpen] = useState(false);

  const anyBookmarked = usePricingBookmarksStore((store) =>
    pairs.some((pair) =>
      selectIsBookmarked(store, pair.variant_pack_id, pair.restaurant_id),
    ),
  );

  // A biz-org selection can span suppliers, so the bulk payload carries the
  // deduped set the same way bulk edits do.
  const analyticsTargets = useMemo(
    () => ({
      restaurantUUIDs: Array.from(
        new Set(pairs.map((pair) => pair.restaurant_id)),
      ),
      supplierUUIDs: Array.from(new Set(supplierUUIDs)),
      variantPackUUIDs: Array.from(
        new Set(pairs.map((pair) => pair.variant_pack_id)),
      ),
    }),
    [pairs, supplierUUIDs],
  );

  const trackBookmark = useCallback(
    (hasNote: boolean) => {
      if (surface === BookmarkSurface.Inline) {
        pricingManagementAnalytics.track(PricingAnalyticEvent.INLINE_BOOKMARK, {
          hasNote,
          pageSource,
          restaurantUUID: pairs[0].restaurant_id,
          supplierUUID: supplierUUIDs[0],
          variantPackUUID: pairs[0].variant_pack_id,
        });
        return;
      }
      pricingManagementAnalytics.track(PricingAnalyticEvent.BULK_BOOKMARK, {
        hasNote,
        pageSource,
        ...analyticsTargets,
      });
    },
    [analyticsTargets, pageSource, pairs, supplierUUIDs, surface],
  );

  const trackUnbookmark = useCallback(() => {
    if (surface === BookmarkSurface.Inline) {
      pricingManagementAnalytics.track(PricingAnalyticEvent.INLINE_UNBOOKMARK, {
        pageSource,
        restaurantUUID: pairs[0].restaurant_id,
        supplierUUID: supplierUUIDs[0],
        variantPackUUID: pairs[0].variant_pack_id,
      });
      return;
    }
    pricingManagementAnalytics.track(PricingAnalyticEvent.BULK_UNBOOKMARK, {
      pageSource,
      ...analyticsTargets,
    });
  }, [analyticsTargets, pageSource, pairs, supplierUUIDs, surface]);

  const bookmarkAction = useMemo<PricingTableQuickAction | null>(() => {
    if (pairs.length === 0) {
      return null;
    }
    if (anyBookmarked) {
      return {
        id: BookmarkAction.Unbookmark,
        label: i18n.t('pricing.bookmarks.action.unbookmark'),
        onSelect: () => {
          unbookmark(pairs);
          trackUnbookmark();
        },
        shortcutKey: QuickActionShortcutKey.B,
      };
    }
    if (!canBookmarkItem) {
      return null;
    }
    return {
      id: BookmarkAction.Bookmark,
      label: i18n.t('pricing.bookmarks.action.bookmark'),
      onSelect: () => {
        pairs.forEach((pair) =>
          setBookmark(pair.variant_pack_id, pair.restaurant_id, null),
        );
        setIsNoteOpen(true);
      },
      shortcutKey: QuickActionShortcutKey.B,
    };
  }, [
    anyBookmarked,
    canBookmarkItem,
    pairs,
    setBookmark,
    trackUnbookmark,
    unbookmark,
  ]);

  // One shared note applied to every pair; single upsert on close. The bookmark
  // event rides along here rather than on `[B]`: this is where the write
  // actually happens, and the only point where the note is known.
  const onSaveNote = useCallback(
    (note: string) => {
      setIsNoteOpen(false);
      if (pairs.length === 0) {
        return;
      }
      bookmark(pairs.map((pair) => ({ ...pair, note: note || null })));
      trackBookmark(Boolean(note));
    },
    [bookmark, pairs, trackBookmark],
  );

  return { bookmarkAction, isNoteOpen, onSaveNote };
};

// Single-row wrapper: the editable pricing cells anchor on one pair and also
// need the row's bookmark state for the in-input icon and note tooltip.
export const useBookmarkAction = ({
  canBookmarkItem,
  pageSource,
  restaurantUUID,
  supplierUUID,
  variantPackUUID,
}: {
  canBookmarkItem: boolean;
  pageSource: PricingPageSource;
  restaurantUUID: string;
  supplierUUID: string;
  variantPackUUID: string;
}) => {
  const pairs = useMemo<BookmarkPair[]>(
    () => [{ restaurant_id: restaurantUUID, variant_pack_id: variantPackUUID }],
    [restaurantUUID, variantPackUUID],
  );
  const supplierUUIDs = useMemo(() => [supplierUUID], [supplierUUID]);
  const { bookmarkAction, isNoteOpen, onSaveNote } = useBookmarkActionForPairs({
    canBookmarkItem,
    pageSource,
    pairs,
    supplierUUIDs,
    surface: BookmarkSurface.Inline,
  });
  const isBookmarked = usePricingBookmarksStore((store) =>
    selectIsBookmarked(store, variantPackUUID, restaurantUUID),
  );
  const bookmarkNote = usePricingBookmarksStore((store) =>
    selectBookmarkNote(store, variantPackUUID, restaurantUUID),
  );

  return { bookmarkAction, bookmarkNote, isBookmarked, isNoteOpen, onSaveNote };
};
